import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import AddForm from "../components/AddForm";
import UpdateForm from "../components/UpdateForm";

// RasPm Dashboard examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { RequestService } from "services/RequestService";

function GTDataTables() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState({
    columns: [
      {
        Header: "#",
        accessor: "index",
        width: "5%",
        sortable: true,
        align: "center",
      },
      {
        Header: "اسم التوربين",
        accessor: "name",
        width: "30%",
        sortable: true,
      },
      {
        Header: "التوربين البخاري",
        accessor: "stName",
        width: "30%",
        sortable: true,
      },
      {
        Header: "اجراء",
        accessor: "actions",
        width: "15%",
        sortable: false,
      }
    ],
    rows: [],
  });

  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "تنبيه",
    content: "",
    dateTime: "",
    open: false,
  });

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [gtToDelete, setGtToDelete] = useState(null);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [gtToUpdate, setGtToUpdate] = useState(null);
  const closeNotification = () => setNotification({ ...notification, open: false });

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleOpenDeleteDialog = (gtId) => {
    setGtToDelete(gtId);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setGtToDelete(null);
  };

  const handleOpenUpdateDialog = (gt) => {
    setGtToUpdate(gt);
    setOpenUpdateDialog(true);
  };

  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
    setGtToUpdate(null);
  };

  const handleDelete = async () => {
    try {
      const response = await RequestService.delete(`GT/${gtToDelete}`);
      if (response.data && !response.data.error) {
        setNotification({
          color: "success",
          icon: "check",
          title: "تمت العملية بنجاح",
          content: response.data?.message || "تم حذف التوربين الغازي بنجاح",
          open: true,
        });
        fetchGTs();
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في حذف التوربين الغازي",
          open: true,
        });
      }
    } catch (error) {
      console.error("Error deleting gas turbine:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء حذف التوربين الغازي",
        open: true,
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleUpdate = async (gt) => {
    try {
      const response = await RequestService.put(`GT/${gt.id}`, gt);
      if (response.data && !response.data.error) {
        setNotification({
          color: "success",
          icon: "check",
          title: "تمت العملية بنجاح",
          content: response.data?.message || "تم تعديل التوربين الغازي بنجاح",
          open: true,
        });
        fetchGTs();
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في تعديل التوربين الغازي",
          open: true,
        });
      }
    } catch (error) {
      console.error("Error updating gas turbine:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء تعديل التوربين الغازي",
        open: true,
      });
    } finally {
      handleCloseUpdateDialog();
    }
  };

  const fetchGTs = async () => {
    setIsLoading(true);
    try {
      const response = await RequestService.get("GT");
      if (response.data && !response.data.error) {
        const gts = response.data.data || [];
        setTableData({
          ...tableData,
          rows: gts.map((gt, index) => ({
            index: index + 1,
            name: gt.name,
            stName: gt.stName,
            actions: (
              <MDBox display="flex" alignItems="center" gap={1}>
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={() => handleOpenUpdateDialog(gt)}
                  startIcon={<Icon>edit</Icon>}
                >
                  تعديل
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleOpenDeleteDialog(gt.id)}
                  startIcon={<Icon>delete</Icon>}
                >
                  حذف
                </MDButton>
              </MDBox>
            ),
          })),
        });
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في تحميل بيانات التوربينات الغازية",
          open: true,
        });
      }
    } catch (error) {
      console.error("Error fetching gas turbines:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء جلب بيانات التوربينات الغازية",
        open: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGTs();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
                sx={{
                  backgroundColor: "grey.100",
                  borderBottom: "1px solid",
                  borderColor: "grey.300",
                }}
              >
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    قائمة التوربينات الغازية
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    إدارة التوربينات الغازية وربطها بالتوربينات البخارية
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleOpenAddDialog}
                  startIcon={<AddIcon />}
                >
                  إضافة توربين
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={tableData}
                  isSorted={true}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <AddForm
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        onSuccess={(message) => {
          fetchGTs();
          handleCloseAddDialog();
          setNotification({
            color: "success",
            icon: "check",
            title: "تمت العملية بنجاح",
            content: message || "تم إضافة التوربين الغازي بنجاح",
            open: true,
          });
        }}
      />
      <UpdateForm
        open={openUpdateDialog}
        onClose={handleCloseUpdateDialog}
        gt={gtToUpdate}
        onSuccess={(gt) => handleUpdate(gt)}
      />
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"تأكيد الحذف"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            هل أنت متأكد من حذف هذا التوربين الغازي؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDeleteDialog} color="primary">
            إلغاء
          </MDButton>
          <MDButton onClick={handleDelete} color="error" autoFocus>
            حذف
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        dateTime={notification.dateTime}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
      />
    </DashboardLayout>
  );
}

export default GTDataTables;
